/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// ONE RECONCILIATION QUESTION, THEN RECORD AND MOVE ON (A4, 11/10/2026). On the
// 5.5 models Alex raised the permission point, took the client's answer, and two
// turns later asked the client to reconcile that answer with how they had first
// described the business (walk wec36d783, message 28). The permission clause had
// already gone from the prompt; nothing told Alex the point was settled.
//
// This builds, from the conversation alone, the list of points that are settled
// and must not be raised again in the conversation:
//   a permission gap the client has answered (permission-gaps.js gapAnswered),
//     with the client's answer;
//   a reconciliation question Alex has asked (it sets an earlier account of the
//     client's against a later one) that the client has replied to, with the
//     question and the reply.
// Both accounts and the answer reach the officer through the interview
// inconsistency capture (api/_interview-inconsistencies.js). Pure; the prompt
// text carries no dash (standing rule 6).

import { ACTIVITIES, permissionRecord, permissionGaps, clientStatements, statedActivities, gapRaised, gapAnswered } from './permission-gaps.js';
import { isTypedSubmit } from './df-submit-gate.js';

const clean = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
const clip = (v, n) => { const s = clean(v); return s.length > n ? s.slice(0, n - 1).replace(/\s+\S*$/, '') + '…' : s; };
const sentences = (p) => String(p || '').split(/(?<=[.?!])\s+/);
const paragraphs = (t) => String(t || '').split(/\n\s*\n/);

// "Earlier you described...", "you said before...", "you mentioned at first...".
export const RECONCILE_CUE = /\b(?:earlier|before|previously|at first|initially|originally)\b[^.?!]{0,80}\byou(?:'ve| have)?\s+(?:said|described|mentioned|told me|stated|explained)\b|\byou(?:'ve| have)?\s+(?:said|described|mentioned|told me|stated)\b[^.?!]{0,80}\b(?:earlier|before|previously|at first|initially)\b/i;

// The client message that answered a permission gap (the one gapAnswered finds).
function gapAnswerText(messages, gap) {
  const a = ACTIVITIES.find(x => x.key === gap.key);
  const list = Array.isArray(messages) ? messages : [];
  const firstStated = list.findIndex(m => m && m.role === 'user' && statedActivities([String(m.content || '')]).some(s => s.key === gap.key));
  if (firstStated < 0) return '';
  const hit = list.find((m, i) => i > firstStated && m && m.role === 'user' && a.mention.test(String(m.content || ''))
    && /\b(?:permission|permissions|variation|authoris\w*|appointed\s+representative|regulator|FCA|register)\b/i.test(String(m.content || '')));
  return hit ? String(hit.content || '') : '';
}

// A reconciliation sets two accounts against each other: the paragraph also
// carries a contrast ("but you've now said", "however", "whereas"), so a plain
// "you mentioned earlier that ..." follow up question is not one.
const CONTRAST = /\b(?:now|but|however|whereas|while|later|different|differs|contradict\w*|inconsistent)\b/i;

// Reconciliation questions Alex asked that the client has replied to.
export function reconciliationsAnswered(messages) {
  const list = Array.isArray(messages) ? messages : [];
  const out = [];
  list.forEach((m, i) => {
    if (!m || m.role !== 'assistant') return;
    const para = paragraphs(m.content).find(p => RECONCILE_CUE.test(p) && CONTRAST.test(p) && sentences(p).some(s => /\?\s*$/.test(s)));
    if (!para) return;
    const reply = list.slice(i + 1).find(x => x && x.role === 'user');
    if (!reply) return;
    // Only the question and the sentence setting the two accounts side by side are
    // quoted back to Alex, never the rest, so nothing it said about the file is put
    // in front of it to repeat.
    const asked = para.split(/(?<=[.?!])\s+/).filter(x => /\?\s*$/.test(x) || RECONCILE_CUE.test(x)).join(' ') || para;
    out.push({ question: asked, answer: String(reply.content || ''), at: i });
  });
  return out;
}

// The prompt block. Empty when nothing is settled.
export function settledClause(board, messages) {
  const items = [];
  if (board && permissionRecord(board)) {
    for (const g of permissionGaps(board, clientStatements(messages))) {
      const ans = gapAnswerText(messages, g);
      if (ans) items.push(`How ${g.label} is carried on. The client answered: "${clip(ans, 300)}"`);
    }
  }
  for (const r of reconciliationsAnswered(messages)) {
    items.push(`You asked: "${clip(r.question, 220)}" The client answered: "${clip(r.answer, 300)}"`);
  }
  if (!items.length) return '';
  return '\n\nSETTLED IN THIS CONVERSATION, DO NOT RAISE AGAIN:\n' + items.map((x, i) => `${i + 1}. ${x}`).join('\n')
    + '\nFor each point above: do not ask about it again in this conversation, do not ask the client to reconcile it with anything they said earlier, and do not restate their earlier account. If the client mentions it again, acknowledge it in one sentence and move on.';
}


// AN UNANSWERED POINT: ONCE PER VISIT, AND ONCE MORE AT SUBMIT (11/10/2026).
// Alex may ask an open permission point the client has not answered once in each
// visit, and once more when the client types SUBMIT without having answered it.
// Not more. A visit is one load of the portal: the page sends visitStart, the
// number of messages it restored when it opened, so messages from visitStart on
// are this visit. The points come from the board and the whole conversation; the
// counting from this visit alone.
export function openPointsInVisit(board, messages, visitStart, ledger = []) {
  const list = Array.isArray(messages) ? messages : [];
  if (!board || !permissionRecord(board)) return [];
  const start = visitStartIndex(list, visitStart);
  const visit = list.slice(start);
  return permissionGaps(board, clientStatements(list))
    .filter(g => !gapAnswered(list, g))
    .map(g => ({
      gap: g,
      label: g.label,
      // A34 (walk we8dd8d41, 16/10/2026): asked is the server's record that the
      // point was put to the client in this visit (ledgerRaised), or Alex's
      // wording naming it. The wording alone missed a reworded ask ("whether any
      // of your clients are corporate clients") and every SUBMIT was held.
      askedThisVisit: ledgerRaised(ledger, g.key, start) || gapRaised(visit, g),
      // An ask that answered a typed SUBMIT in this visit.
      askedAtSubmit: ledgerHeld(ledger, start, g.key) || visit.some((m, i) => m && m.role === 'user' && isTypedSubmit(m.content) && visit[i + 1] && gapRaised([visit[i + 1]], g)),
    }));
}

// A VISIT is one load of the client portal. The page counts the messages it
// restored when it opened (visitStart); every message from that index on is
// this visit. A reload, or a return on another day, starts a new visit. With
// no count from the page the whole conversation is one visit.
export function visitStartIndex(messages, visitStart) {
  const n = Array.isArray(messages) ? messages.length : 0;
  return Number.isInteger(visitStart) && visitStart >= 0 && visitStart <= n ? visitStart : 0;
}

// THE OPEN POINT LEDGER (collected_data.openPointLedger, server written, A34).
// One entry each time the server put a point to the client: 'raised' when the
// turn's instructions told Alex to raise it in that reply, 'submit_hold' when a
// typed SUBMIT was held for it (the held reply is fixed text naming it, so it
// was certainly said). index is the number of messages before that turn, so an
// entry belongs to the visit that starts at or before it.
const LEDGER_KINDS = ['raised', 'submit_hold'];
const asLedger = (l) => (Array.isArray(l) ? l : []).filter(e => e && typeof e === 'object' && typeof e.key === 'string' && LEDGER_KINDS.includes(e.kind));
export function ledgerRaised(ledger, key, fromIndex = 0) {
  return asLedger(ledger).some(e => e.key === key && (!fromIndex || (Number.isInteger(e.index) && e.index >= fromIndex)));
}
export function ledgerHeld(ledger, fromIndex = 0, key = null) {
  return asLedger(ledger).some(e => e.kind === 'submit_hold' && (key === null || e.key === key) && (!fromIndex || (Number.isInteger(e.index) && e.index >= fromIndex)));
}
export function ledgerEntries(points, kind, index, at) {
  return (points || []).filter(p => p && p.key).map(p => ({ key: String(p.key), label: String(p.label || ''), kind, index: Number.isInteger(index) ? index : null, at }));
}

// A34 SAFETY NET: A TYPED SUBMIT IS HELD AT MOST ONCE PER VISIT for an open
// point. Held already when the ledger records a hold in this visit, or (so a
// failed ledger write cannot hold the client again and again) when a typed
// SUBMIT in this visit was answered by the held reply itself: the server's own
// fixed text (submitHoldText), never Alex's wording. The analysis hold (the
// documents are still being read) is not a hold for a point and does not count.
export const SUBMIT_POINT_HOLD_LEAD = 'Your file has not been submitted yet. One thing is still needed:';
export function heldThisVisit(messages, visitStart, ledger = []) {
  const list = Array.isArray(messages) ? messages : [];
  const start = visitStartIndex(list, visitStart);
  if (ledgerHeld(ledger, start)) return true;
  const visit = list.slice(start);
  return visit.some((m, i) => m && m.role === 'user' && isTypedSubmit(m.content)
    && visit[i + 1] && visit[i + 1].role === 'assistant' && String(visit[i + 1].content || '').trim().startsWith(SUBMIT_POINT_HOLD_LEAD));
}

// What a typed SUBMIT the page has cleared to go ('submit') does on a documents
// first case:
//   hold  the first SUBMIT of the visit while a permission point is open (on the
//         first submission or a return visit): the reply is fixed text
//         (submitHoldText), no model writes it
//   go    the first submission, at any later SUBMIT in the visit or with nothing
//         open: the file is sent, and any point still open is recorded for the
//         officer (points)
//   none  already submitted and nothing to hold (a return visit, as before)
export function decideTypedSubmit({ board, messages, visitStart, ledger = [], submittedAt = null }) {
  const points = openPointsInVisit(board, messages, visitStart, ledger);
  // A point already put to the client at a SUBMIT in this visit (askedAtSubmit)
  // does not hold again, and nothing holds twice in a visit (heldThisVisit).
  if (points.some(p => !p.askedAtSubmit) && !heldThisVisit(messages, visitStart, ledger)) return { action: 'hold', points };
  if (!submittedAt) return { action: 'go', points };
  return { action: 'none', points };
}

const joinAnd = (xs) => xs.length <= 1 ? (xs[0] || '') : xs.slice(0, -1).join(', ') + ' and ' + xs[xs.length - 1];
// The held reply: plain that nothing has been sent, the one thing needed, and
// that the next SUBMIT sends the file as it is. No dash (standing rule 6).
// HOTFIX (22/10/2026): the held SUBMIT reply goes to the client word for word,
// and the regulator name in it was the board's own field
// (regulatoryStatus.regulator), which the analysis model writes. A value such as
// "FCA. possible OFAC hit." went out as it was. The reply now names a regulator
// only from this accepted list, and always in the list's own wording, never the
// board's string. Anything else, a status in brackets included, gets the general
// wording ("the regulator record we hold"), which is always true; the
// replacement is logged with a count only. Fails closed: a real regulator that
// is not listed only loses its name. Add names here as firms need them.
const HOLD_REGULATORS = new Map(Object.entries({
  'fca': 'FCA', 'financial conduct authority': 'Financial Conduct Authority',
  'pra': 'PRA', 'prudential regulation authority': 'Prudential Regulation Authority',
  'fca and pra': 'FCA and PRA', 'pra and fca': 'PRA and FCA',
  'cysec': 'CySEC', 'cyprus securities and exchange commission': 'Cyprus Securities and Exchange Commission',
  'bafin': 'BaFin', 'bundesanstalt fur finanzdienstleistungsaufsicht': 'BaFin',
  'finma': 'FINMA', 'swiss financial market supervisory authority': 'FINMA',
  'vqf': 'VQF', 'polyreg': 'PolyReg', 'so fit': 'SO FIT', 'sro vqf': 'VQF',
  'central bank of ireland': 'Central Bank of Ireland',
  'mfsa': 'MFSA', 'malta financial services authority': 'Malta Financial Services Authority',
  'gfsc': 'GFSC', 'gibraltar financial services commission': 'Gibraltar Financial Services Commission',
  'jfsc': 'JFSC', 'jersey financial services commission': 'Jersey Financial Services Commission',
  'gfsc guernsey': 'Guernsey Financial Services Commission', 'guernsey financial services commission': 'Guernsey Financial Services Commission',
  'iomfsa': 'Isle of Man Financial Services Authority', 'isle of man financial services authority': 'Isle of Man Financial Services Authority',
  'dfsa': 'DFSA', 'dubai financial services authority': 'Dubai Financial Services Authority',
  'fsra': 'FSRA', 'vara': 'VARA', 'sca': 'SCA',
  'cssf': 'CSSF', 'commission de surveillance du secteur financier': 'CSSF',
  'amf': 'AMF', 'autorite des marches financiers': 'AMF',
  'afm': 'AFM', 'autoriteit financiele markten': 'AFM', 'dnb': 'DNB', 'de nederlandsche bank': 'DNB',
  'consob': 'CONSOB', 'cnmv': 'CNMV', 'bank of lithuania': 'Bank of Lithuania', 'fma': 'FMA',
  'sfc': 'SFC', 'securities and futures commission': 'Securities and Futures Commission',
  'mas': 'MAS', 'monetary authority of singapore': 'Monetary Authority of Singapore',
  'asic': 'ASIC', 'cima': 'CIMA', 'cayman islands monetary authority': 'Cayman Islands Monetary Authority',
  'bvi fsc': 'BVI FSC', 'svg fsa': 'SVG FSA',
  'sec': 'SEC', 'finra': 'FINRA', 'cftc': 'CFTC', 'nfa': 'NFA',
}));
const holdRegulatorKey = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  .replace(/[,/&]+/g, ' and ').replace(/[.']/g, '').replace(/\s+/g, ' ').trim();
// The same regulator under its short and full name, and the countries a
// bracket may name, so "Financial Conduct Authority (FCA)" and "Financial
// Conduct Authority (UK)" keep the name while "FCA (lapsed)" does not.
const HOLD_SAME = [['FCA', 'Financial Conduct Authority'], ['PRA', 'Prudential Regulation Authority'], ['DFSA', 'Dubai Financial Services Authority'],
  ['MFSA', 'Malta Financial Services Authority'], ['MAS', 'Monetary Authority of Singapore'], ['SFC', 'Securities and Futures Commission'],
  ['CySEC', 'Cyprus Securities and Exchange Commission'], ['CIMA', 'Cayman Islands Monetary Authority'], ['GFSC', 'Gibraltar Financial Services Commission'],
  ['JFSC', 'Jersey Financial Services Commission']];
const HOLD_COUNTRY = new Set(['uk', 'united kingdom', 'gb', 'great britain', 'ireland', 'malta', 'cyprus', 'germany', 'switzerland', 'gibraltar', 'jersey', 'guernsey', 'isle of man', 'singapore', 'hong kong', 'dubai', 'uae', 'luxembourg', 'france', 'netherlands', 'cayman islands', 'usa', 'us', 'united states']);
const holdSame = (a, b) => a === b || HOLD_SAME.some(([x, y]) => (a === x && b === y) || (a === y && b === x));
function holdLookup(v) {
  const m = v.match(/^([^()]+?)\s*\(([^()]+)\)\s*(?:,\s*([^(),]+))?$/);
  if (!m) return /[()]/.test(v) ? undefined : HOLD_REGULATORS.get(holdRegulatorKey(v));
  const outer = HOLD_REGULATORS.get(holdRegulatorKey(m[1]));
  if (!outer) return undefined;
  const innerKey = holdRegulatorKey(m[2]);
  const innerOk = HOLD_COUNTRY.has(innerKey) || (HOLD_REGULATORS.has(innerKey) && holdSame(outer, HOLD_REGULATORS.get(innerKey)));
  const tailOk = !m[3] || HOLD_COUNTRY.has(holdRegulatorKey(m[3]));
  return innerOk && tailOk ? outer : undefined;
}
export function holdRegulatorName(value) {
  const v = typeof value === 'string' ? value.trim() : '';
  if (!v || v === 'the regulator') return '';
  const hit = holdLookup(v);
  if (!hit) {
    console.warn('[submit-hold] the regulator on the board is not on the accepted list; the reply uses the general wording (' + v.length + ' characters)');
    return '';
  }
  return hit;
}
export function submitHoldText(points, board) {
  const rec = permissionRecord(board);
  const labels = (points || []).map(p => p.label).filter(Boolean);
  const name = rec ? holdRegulatorName(rec.regulator) : '';
  const regulator = name ? 'the ' + name + ' record we hold' : 'the regulator record we hold';
  return SUBMIT_POINT_HOLD_LEAD + ' you described ' + joinAnd(labels) + ', and ' + regulator
    + (labels.length > 1 ? ' does not show a permission for these, so please tell me how each is carried on.' : ' does not show a permission for it, so please tell me how that activity is carried on.')
    + ' You can answer now, or type SUBMIT again and your file will be sent as it is.';
}

// THE OFFICER'S FLAG (A34). A case the client submitted while a point was open
// (collected_data.submittedWithOpenPoints, written by the server when that
// SUBMIT went) shows the point on the case. Null when there is nothing to show.
export const CASE_SUBMITTED_STATUSES = ['completed', 'pending_info', 'approved', 'declined', 'pending_director_review', 'director_returned', 'pending_external', 'jaybee_approved', 'jaybee_declined'];
export function submittedOpenPointsNotice(session) {
  const cd = (session && session.collected_data) || {};
  const rec = cd.submittedWithOpenPoints;
  const points = rec && Array.isArray(rec.points) ? rec.points.filter(p => p && p.label) : [];
  if (!points.length) return null;
  if (!cd.submittedAt && !CASE_SUBMITTED_STATUSES.includes(String((session && session.status) || ''))) return null;
  const labels = points.map(p => 'how ' + p.label + ' is carried on');
  const held = points.some(p => p.heldAtSubmit);
  return {
    title: 'Submitted with ' + (points.length === 1 ? 'a point' : points.length + ' points') + ' not answered',
    text: 'The client sent the file while ' + (points.length === 1 ? 'this point was' : 'these points were') + ' still open: ' + joinAnd(labels)
      + '. The regulator record on file does not show a permission for ' + (points.length === 1 ? 'it' : 'them') + '.'
      + (held ? ' It was put to the client when they typed SUBMIT, and they chose to send the file as it is.' : ''),
  };
}

// The line on every turn once an open point has had its ask in this visit.
export function askedThisVisitClause(points) {
  const asked = (points || []).filter(p => p.askedThisVisit);
  if (!asked.length) return '';
  return '\n\nALREADY ASKED IN THIS VISIT, NOT YET ANSWERED: ' + asked.map(p => 'how ' + p.label + ' is carried on').join('; ')
    + '. You have asked this in this visit. Do not ask it again in this visit. The client may answer it, or send their file without answering it.';
}

// The instruction for a SUBMIT that is held for one open point.
export function submitOpenPointClause(point) {
  return '\n\nTHE CLIENT HAS TYPED SUBMIT, AND THE FILE HAS NOT BEEN SENT: one point is still unanswered: how ' + point.label
    + ' is carried on. Ask it once more, briefly and without pressure, as the only question in this reply. Tell them they can answer it, or type SUBMIT again to send the file as it is. Do not say the file has been sent, and do not say how the answer will be used or judged.';
}
